/**
 * Wrapper for the FinixReader Capacitor plugin (capacitor-finix-reader/),
 * which drives Bluetooth card readers (PAX D135) through Finix's native
 * mobile SDKs. The plugin only exists in native app builds — web sessions
 * must check isFinixReaderAvailable() and steer staff to the app.
 *
 * Card data never touches this layer: the native SDK owns the interaction
 * and creates the transfer; JS sees only status and the transfer id, which
 * the backend verifies before crediting.
 */
import { Capacitor, registerPlugin } from '@capacitor/core'
import type { TerminalSdkSession } from '@/lib/api/types'

export interface FinixReaderDevice {
  /** Bluetooth identifier used for connect() */
  id: string
  name?: string
}

export interface FinixReaderPaymentResult {
  /** Finix transfer id (TRxxx) created by the SDK */
  transferId: string
  state?: string
}

export interface FinixReaderPlugin {
  /** Configure the SDK with a backend-minted session (credentials, MU/MID, DV id). */
  initialize(options: TerminalSdkSession): Promise<void>
  /** Scan for nearby readers. */
  discoverReaders(): Promise<{ devices: FinixReaderDevice[] }>
  /**
   * Connect to a reader. Without an id, connects to the first discovered
   * (v1: one paired reader per phone).
   */
  connect(options?: { deviceId?: string }): Promise<{ device: FinixReaderDevice }>
  /** Run a sale on the connected reader. Resolves when the SDK finishes. */
  startPayment(options: {
    amountCents: number
    currency: string
    idempotencyKey?: string
    tags?: Record<string, string>
  }): Promise<FinixReaderPaymentResult>
  /** Cancel the in-flight interaction, if any. */
  cancel(): Promise<void>
  disconnect(): Promise<void>
}

export const FinixReader = registerPlugin<FinixReaderPlugin>('FinixReader')

export function isFinixReaderAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('FinixReader')
}
